// Copyright 2022-2026 - Hewlett Packard Enterprise Company
import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';
/* eslint-disable import/no-unresolved */
import resourcesToBackend from 'i18next-resources-to-backend';

// when REACT_APP_ADD_NON_PROD_I18N_LANGS is "true", COM will be able to support
// all languages in nonProdLangs array, in a nonprod environment.
const NON_PROD_I18N_LANGS = window.indigo.REACT_APP_ADD_NON_PROD_I18N_LANGS;

const supportedLngs = ['en-US', 'ja-JP'];
const nucleonSupportedLngsMap = {
  en: 'en-US',
  ja: 'ja-JP',
  zh: 'zh-CN',
};
// The following are languages that should not be delivered to customers
// es-ES - Spanish to test local to indigo non-English l10n
const nonProdLangs = ['es-ES'];
if (NON_PROD_I18N_LANGS && NON_PROD_I18N_LANGS === 'true') {
  supportedLngs.push(...nonProdLangs);
}

// Product-specific namespace configuration
// For NUCLEON builds, 'nucleon' namespace is checked first, then falls back to 'common'
// eslint-disable-next-line no-undef
const PRODUCT = __PRODUCT__;
export const I18N_NAMESPACES = ['common'];

if (PRODUCT === 'NUCLEON') {
  I18N_NAMESPACES.unshift('nucleon');
}

if (PRODUCT === 'HELIX') {
  I18N_NAMESPACES.unshift('helix');
}

function createModuleNotFoundError() {
  return Object.assign(new Error('Module not found'), {
    code: 'MODULE_NOT_FOUND',
  });
}

function getNamespaceLoaders() {
  if (PRODUCT === 'NUCLEON') {
    return {
      common: language => import(`./locales/${language}/common.json`),
      nucleon: language => import(`./locales/${language}/nucleon.json`),
    };
  }

  if (PRODUCT === 'HELIX') {
    return {
      common: language => import(`./locales/${language}/common.json`),
      helix: language => import(`./locales/${language}/helix.json`),
    };
  }

  return {
    common: language => import(`./locales/${language}/common.json`),
  };
}

const PRODUCT_NAMESPACE_LOADERS = getNamespaceLoaders();

async function loadNamespaceResources(language, namespace) {
  const namespaceLoader = PRODUCT_NAMESPACE_LOADERS[namespace];

  if (namespaceLoader) {
    return namespaceLoader(language);
  }

  throw createModuleNotFoundError();
}

function normalizeNucleonLocale(locale) {
  if (!locale) {
    return 'en-US';
  }
  const [language] = locale.toLowerCase().split('-');
  return nucleonSupportedLngsMap[language] || 'en-US';
}

const browserLocale =
  typeof navigator !== 'undefined'
    ? navigator.languages?.[0] || navigator.language
    : 'en-US';

i18n
  .use(
    resourcesToBackend(async (language, namespace, callback) => {
      try {
        const resources = await loadNamespaceResources(language, namespace);
        callback(null, resources);
      } catch (error) {
        // For non-common namespaces, only treat "module not found" as an empty bundle
        if (namespace !== 'common') {
          const isModuleNotFound =
            error.message?.includes('Cannot find module') ||
            error.message?.includes(
              'Failed to fetch dynamically imported module',
            ) ||
            error.code === 'MODULE_NOT_FOUND';

          if (isModuleNotFound) {
            callback(null, {});
            return;
          }
        }
        callback(error, null);
      }
    }),
  )
  // detect user language
  // learn more: https://github.com/i18next/i18next-browser-languageDetector
  .use(LanguageDetector)
  // pass the i18n instance to react-i18next.
  .use(initReactI18next)
  // init i18next
  // for all options read: https://www.i18next.com/overview/configuration-options
  .init({
    lng:
      PRODUCT === 'NUCLEON' ? normalizeNucleonLocale(browserLocale) : undefined,
    fallbackLng: 'en-US',
    supportedLngs,
    ns: I18N_NAMESPACES,
    defaultNS: I18N_NAMESPACES[0],
    fallbackNS: 'common',
    detection: {
      // a different detection order and caches can be found here https://github.com/glcp/frontend/blob/mainline/ui/src/i18n.js
      order:
        PRODUCT === 'NUCLEON'
          ? ['navigator']
          : ['cookie', 'localStorage', 'navigator', 'htmlTag'],
      // after the language code was detected, it will be stored in the localStorage and cookie
      caches: PRODUCT === 'NUCLEON' ? [] : ['cookie', 'localStorage'],
    },
    debug: false,
    interpolation: {
      escapeValue: false, // not needed for react as it escapes by default
    },
    react: {
      useSuspense: true,
    },
  });

export default i18n;
